<template>
    <MainContent
        title="Campañas de Upselling"
        searchable
        v-model="filtersLocal"
        :meta="loading ? {} : campaigns.meta"
        @updated="queueCampaignsReload"
        @input="value => Object.assign(filtersLocal, value)"
    >
        <template #header>
            <div class="tw:flex tw:flex-col tw:gap-6 tw:w-full">
                <div class="tw:grid tw:grid-cols-1 tw:md:grid-cols-2 tw:gap-6 tw:w-full">
                    <StatsCard
                        title="Mensajes enviados"
                        :value="detalles_economicos.mensajes_enviados"
                        :loading="loadingEstadisticas"
                    />
                    <StatsCard
                        title="Coste total"
                        :value="detalles_economicos.coste_total | formatear_precio"
                        :loading="loadingEstadisticas"
                    />
                </div>
            </div>
        </template>
        <template #filters>
            <div class="tw:flex tw:flex-wrap tw:gap-3 tw:md:gap-2 tw:items-center">
                <div class="tw:w-full tw:sm:w-48">
                    <BaseMultiselect
                        name="estado"
                        label="Estado"
                        :options="[
                            { id: '1', label: 'Activa' },
                            { id: '0', label: 'Inactiva' }
                        ]"
                        value-key="id"
                        label-key="label"
                        :value="filtersLocal.activo"
                        :disabled="disabled"
                        @input="val => filtersLocal.activo = val"
                    />
                </div>
                <div class="tw:w-full tw:sm:w-48">
                    <BaseMultiselect
                        name="disparador"
                        label="Disparador"
                        :options="[
                            { id: 'eventos', label: 'Eventos' },
                            { id: 'abonos', label: 'Abonos' },
                            { id: 'bases', label: 'Deportistas' }
                        ]"
                        value-key="id"
                        label-key="label"
                        :value="filtersLocal.tipo_campana"
                        :disabled="disabled"
                        @input="val => filtersLocal.tipo_campana = val"
                    />
                </div>
                <div class="tw:w-full tw:sm:w-56">
                    <BaseMultiselect
                        name="promotores"
                        label="Promotores"
                        value-key="id"
                        label-key="nombre"
                        multiple
                        :value="filtersLocal.teatro_company_ids"
                        :disabled="loadingCompanias"
                        :options="companies"
                        @input="val => filtersLocal.teatro_company_ids = val"
                    />
                </div>
                <Button
                    :disabled="disabled"
                    @click="resetFilters"
                >
                    Limpiar filtros
                </Button>
                <Button
                    as="router-link"
                    :disabled="disabled"
                    :to="{ name: 'back.upselling.create' }"
                >
                    + Nueva campaña
                </Button>
            </div>
        </template>
        <div class="tw:bg-white tw:rounded-xl tw:shadow-sm">
            <BaseTable
                :columns="columns"
                :data="campaigns.data"
                :loading="loading"
                emptyText="No hay campañas disponibles"
                :default-col-width="defaultColWidth"
            >
                <template #cell-estado="{ row }">
                    <ToggleSwitch
                        size="lg"
                        :value="optimisticActivas[row.id] ?? row.activa"
                        :disabled="isToggling(row.id) || disabled"
                        @input="val => toggleCampaign(row, val)"
                    />
                </template>
                <template #cell-canales="{ row }">
                    <span class="tw:flex tw:items-center tw:gap-2">
                        <i v-if="row.tipo_whatsapps" class="fab fa-whatsapp tw:text-green-500" title="WhatsApp"></i>
                        <i v-if="row.tipo_emails" class="fas fa-envelope tw:text-blue-500" title="Email"></i>
                        <span v-if="!row.tipo_whatsapps && !row.tipo_emails" class="tw:text-xs tw:text-gray-400">-</span>
                    </span>
                </template>
                <template #cell-retraso="{ row }">
                    {{ row.retraso_envio > 0 ? row.retraso_envio + ' minutos' : 'Sin retraso' }}
                </template>
                <template #cell-acciones="{ row, index }">
                    <OptionsDropdownComponent
                        :lastIndex="campaigns.data.length > 3 && index >= campaigns.data.length - 3"
                        :options="[
                            {
                                to: { name: 'back.upselling.show', params: { campaign_id: row.id } },
                                label: 'Ver'
                            },
                            {
                                to: { name: 'back.upselling.update', params: { campaign_id: row.id } },
                                label: 'Editar'
                            },
                            {
                                type: 'action',
                                action: () => showModal('eliminar-campana-' + row.id, row.id),
                                label: 'Eliminar',
                                danger: true
                            }
                        ]"
                    />
                </template>
            </BaseTable>
        </div>

        <BaseModal
            :show="!!campanaAEliminar"
            title="Eliminar campaña"
            message="¿Estás seguro de que deseas eliminar esta campaña?"
            confirm-text="SI, BORRAR"
            confirm-variant="danger"
            @close="closeModal"
            @confirm="confirmarEliminacion"
        />
    </MainContent>
</template>

<script setup>
    import { watch, onMounted, onUnmounted, ref } from 'vue'
    import debounce from 'lodash/debounce'
    import Button from '@/modules/admin/ui/Button.vue'
    import BaseTable from '@/modules/admin/ui/table/BaseTable.vue'
    import BaseMultiselect from '@/modules/admin/ui/BaseMultiselect.vue'
    import ToggleSwitch from '@/modules/admin/ui/ToggleSwitch.vue'
    import useFilters from '@/composables/common/useFilters'
    import { useToast } from '@/composables/common/useToast'
    import StatsCard from '@/modules/admin/ui/StatsCard.vue'
    import MainContent from '@/modules/admin/ui/MainContent.vue'
    import OptionsDropdownComponent from '@/modules/admin/ui/OptionsDropdownComponent.vue'
    import BaseModal from '@/modules/admin/ui/BaseModal.vue'
    import useCampaign from '@/composables/admin/upselling/useCampaign'
    import store from '@/store'

    defineProps({
        defaultColWidth: {
            type: String,
            default: '120px'
        }
    })

    const toast = useToast()

    const campanaAEliminar = ref(null)

    const {
        loading,
        loadingEstadisticas,
        loadingCompanias,
        filters,
        campaigns,
        columns,
        companies,
        detalles_economicos,
        disabled,
        getEstadisticas,
        getCampaigns,
        getCompanies,
        deleteCampaign,
        optimisticActivas,
        isToggling,
        toggleCampaign,
    } = useCampaign()

    const { filtersLocal, applyFilters, resetFilters } = useFilters(filters)

    function getAll() {
        getEstadisticas()
        getCampaigns()
    }

    function queueCampaignsReload() {
        loading.value = true
        getCampaignsDebounced()
    }

    function showModal(name, campana_id) {
        campanaAEliminar.value = campana_id
    }

    function closeModal() {
        campanaAEliminar.value = null
    }

    async function confirmarEliminacion() {
        try {
            await deleteCampaign(campanaAEliminar.value)
            toast.success('Campaña eliminada correctamente')
            closeModal()
        } catch (error) {
            toast.error('Error al eliminar la campaña')
        }
    }

    const getCampaignsDebounced = debounce(() => {
        getAll()
    }, 500)

    onMounted(() => {
        getAll()
        getCompanies()
    })

    onUnmounted(() => {
        getCampaignsDebounced.cancel()
    })

    watch(filtersLocal, () => {
        applyFilters()
        queueCampaignsReload()
    }, { deep: true })
</script>
